import { Month } from '@ynab/core'
import type { MeResponse } from '../api.ts'
import { useTheme } from '../lib/useTheme.ts'

interface HeaderProps {
  me: MeResponse | undefined
  onSync: () => void
  syncing: boolean
  syncError: string | null
  /** Only Looking back pages through months; every other screen is about now. */
  month: { value: string; first: string | undefined; onPrev: () => void; onNext: () => void } | null
}

/**
 * Where the numbers come from, and how fresh they are.
 *
 * Said on every screen because it is the one thing that could make every figure
 * wrong at once: a mirror that stopped syncing looks exactly like a quiet week.
 */
export function Header({ me, onSync, syncing, syncError, month }: HeaderProps) {
  const synced = me?.budget?.lastSyncedAt ?? null
  const failed = me?.lastRun?.status === 'failed'

  return (
    <header className="sticky top-0 z-20 flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-line bg-paper px-4 py-3 sm:px-7 sm:py-4">
      {month && (
        <div className="flex items-center gap-2.5">
          <MonthButton
            label="Previous month"
            onClick={month.onPrev}
            disabled={month.first !== undefined && month.value <= month.first}
          >
            ‹
          </MonthButton>
          <div className="min-w-0 whitespace-nowrap text-center font-display text-[20px] sm:min-w-[150px] sm:text-[23px]">
            {Month.formatLong(Month.budgetMonth(month.value))}
          </div>
          <MonthButton label="Next month" onClick={month.onNext}>
            ›
          </MonthButton>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted">
          {me?.fixture ? 'Invented budget — no YNAB token set' : 'From YNAB'}
        </span>
        <span className="truncate text-[13.5px]">
          {me?.budget ? me.budget.name : 'Not synced yet'}
          {synced !== null && <span className="text-muted"> · {relative(synced)}</span>}
        </span>
      </div>

      {(failed || syncError) && (
        <span className="rounded-full bg-blush-soft px-3 py-1 text-[12px] text-blush-deep">
          {syncError ?? `Last sync failed: ${me?.lastRun?.error ?? 'unknown error'}`}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2.5">
        <button
          type="button"
          onClick={onSync}
          disabled={syncing}
          aria-label={syncing ? 'Syncing' : 'Sync from YNAB'}
          className="flex items-center gap-2 whitespace-nowrap rounded-full border border-line bg-card px-3 py-2 text-[13px] text-ink transition-colors hover:bg-sand disabled:opacity-50 sm:px-3.5"
        >
          <span aria-hidden className={syncing ? 'kete-spin' : undefined}>
            ↻
          </span>
          <span className="hidden sm:inline">{syncing ? 'Syncing…' : 'Sync from YNAB'}</span>
        </button>
        <ThemeToggle />
      </div>
    </header>
  )
}

/** "4 min ago" — freshness is what the reader needs, not a timestamp to subtract from. */
function relative(at: number): string {
  const minutes = Math.round((Date.now() - at) / 60_000)
  if (minutes < 1) return 'synced just now'
  if (minutes < 60) return `synced ${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `synced ${hours} h ago`
  return `synced ${Math.round(hours / 24)} days ago`
}

/**
 * Light or dark, and back to following the OS.
 *
 * Click flips to the opposite of what you can see. Right-click hands control
 * back to the system — a small target for a rare action, and the tooltip says so
 * rather than spending header width on a third button.
 */
function ThemeToggle() {
  const { preference, resolved, toggle, choose } = useTheme()
  const following = preference === 'system'

  return (
    <button
      type="button"
      onClick={toggle}
      onContextMenu={(event) => {
        event.preventDefault()
        choose('system')
      }}
      aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} mode`}
      title={
        following
          ? 'Following your system. Click for light or dark.'
          : `${resolved === 'dark' ? 'Dark' : 'Light'} mode. Click to switch, right-click to follow your system.`
      }
      className="h-[34px] w-[34px] flex-none rounded-full border border-line bg-card text-[14px] leading-none text-ink transition-colors hover:bg-sand"
    >
      {resolved === 'dark' ? '☀' : '☾'}
    </button>
  )
}

function MonthButton({
  label,
  onClick,
  children,
  disabled = false,
  title,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
  disabled?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      {...(title ? { title } : {})}
      className="h-7 w-7 rounded-lg border border-line bg-card text-[13px] text-ink transition-colors hover:bg-sand disabled:opacity-30 disabled:hover:bg-card"
    >
      {children}
    </button>
  )
}
